import { ref, computed } from 'vue';

/**
 * Composable for managing field validation state and behavior
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.fieldName - Unique identifier for the field (used for ARIA attributes)
 * @param {Array<{validate: Function, message: string}>} options.rules - Array of validation rules
 * @param {string} [options.initialValue=''] - Initial value for the field
 * 
 * @returns {Object} Validation state and methods
 * 
 * @example
 * const nameField = useFieldValidation({
 *   fieldName: 'name',
 *   rules: [
 *     { validate: (v) => v.length > 0, message: 'Name is required' },
 *     { validate: (v) => v.includes(' '), message: 'Enter full name' }
 *   ]
 * });
 */
export function useFieldValidation({ fieldName, rules, initialValue = '' }) {
  // Reactive state
  const value = ref(initialValue);
  const errorMessage = ref('');
  const isTouched = ref(false);
  const isDirty = ref(false);
  
  // Computed properties
  const hasError = computed(() => errorMessage.value !== '');
  
  const ariaAttributes = computed(() => ({
    'aria-invalid': hasError.value ? 'true' : 'false',
    'aria-describedby': hasError.value ? `${fieldName}-error` : undefined
  }));
  
  /**
   * Validates the current field value against all rules
   * Runs rules in sequence and returns on first failure
   * 
   * @returns {boolean} True if validation passes, false otherwise
   */
  const validate = () => {
    try {
      // Run through rules in order, return first error
      for (const rule of rules) {
        if (!rule.validate(value.value)) {
          errorMessage.value = rule.message;
          return false;
        }
      }
      
      // All rules passed
      errorMessage.value = '';
      return true;
    } catch (error) {
      console.error(`Validation error for field "${fieldName}":`, error);
      errorMessage.value = 'An error occurred during validation';
      return false;
    }
  };
  
  /**
   * Handles blur event (when user leaves the field)
   * Marks field as touched and triggers validation
   */
  const handleBlur = () => {
    isTouched.value = true;
    validate();
  };
  
  /**
   * Handles input event (when user types in the field)
   * Only re-validates if field has been touched and has errors
   * This prevents intrusive validation while typing initially
   * 
   * @param {string} newValue - The new input value
   */
  const handleInput = (newValue) => {
    value.value = newValue;
    isDirty.value = true;
    
    // Only re-validate if field has been touched and has errors
    // This provides immediate feedback when correcting errors
    if (isTouched.value && hasError.value) {
      validate();
    }
  };
  
  /**
   * Resets the field to its initial state
   * Clears all validation state and errors
   */
  const reset = () => {
    value.value = initialValue;
    errorMessage.value = '';
    isTouched.value = false;
    isDirty.value = false;
  };
  
  return {
    // State
    value,
    errorMessage,
    hasError,
    isTouched,
    isDirty,
    
    // Methods
    handleBlur,
    handleInput,
    validate,
    reset,
    
    // Computed
    ariaAttributes
  };
}
